import {
  ArrowLeft,
  ArrowRight,
  Bot,
  Plus,
  Save,
  Trash2,
  X,
} from 'lucide-react';
import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import { useForm } from 'react-hook-form';
import toast from 'react-hot-toast';
import { useTranslation } from 'react-i18next';
import { useOrganization } from '../../contexts';
import {
  AgentService,
  crewOrderService,
  crewService,
  TaskService,
} from '../../services';
import { Agent, CrewCategory } from '../../types/entities';
import { generateId } from '../../utils';
import AgentFormPanel, { AgentFormValues } from '../agents/AgentFormPanel';
import TaskFormPanel, { TaskFormValues } from '../tasks/TaskFormPanel';
import { Badge, Button, ConfirmModal, Input, Modal } from './';
import AgentSelector from './AgentSelector';

interface WorkflowNode {
  id: string;
  type: 'agent';
  name: string;
  description?: string;
  role?: string;
  goal?: string;
  tools?: string[];
  x: number;
  y: number;
  agentId?: number; // ID do agente original
}

type CreationMode = 'ai' | 'manual';
const DEFAULT_MAX_ITER = '5';
const DEFAULT_MAX_EXECUTION_TIME = '300';
const ENABLED_CREW_TOOLS = [
  'ragtool',
  'codeinterpretertool',
  'filereadtool',
  'directoryreadtool',
  'directorysearchtool',
  'csvsearchtool',
  'jsonsearchtool',
  'txtsearchtool',
  'xmlsearchtool',
  'mdxsearchtool',
  'pdfsearchtool',
  'docxsearchtool',
  'websitesearchtool',
  'scrapewebsitetool',
  'scrapeelementfromwebsitetool',
  'youtubechannelsearchtool',
  'youtubevideosearchtool',
  'githubsearchtool',
] as const;

interface WorkflowBuilderProps {
  onSave: (workflow: {
    agents: WorkflowNode[];
    tasks: WorkflowNode[];
    connections: { from: string; to: string }[];
    executionOrder?: Array<{ agent_id: number; execution_order: number }>;
    crewName?: string;
    crewDescription?: string;
    category_ids?: number[];
  }) => void;
  onCancel: () => void;
  initialData?: {
    agents: WorkflowNode[];
    tasks: WorkflowNode[];
    connections: { from: string; to: string }[];
    crewName?: string;
    crewDescription?: string;
    category_ids?: number[];
  };
  crewId?: number;
  isEditing?: boolean;
  availableCategories?: CrewCategory[];
  organizationId?: number;
  onCategoriesUpdated?: () => Promise<void> | void;
}

/** Normaliza nodes: garante agentId a partir de id quando no formato agent_X. */
function normalizeWorkflowNodes(nodes: WorkflowNode[]): WorkflowNode[] {
  return nodes.map(n => {
    if (n.agentId != null) return n;
    const m = n.id.match(/^agent_(\d+)$/);
    return m ? { ...n, agentId: parseInt(m[1], 10) } : n;
  });
}

export const SimpleWorkflowBuilder: React.FC<WorkflowBuilderProps> = ({
  onSave,
  onCancel,
  initialData,
  crewId,
  isEditing = false,
  availableCategories = [],
  organizationId,
  onCategoriesUpdated,
}) => {
  const { t } = useTranslation();
  const { currentOrganization } = useOrganization();
  const agentService = useMemo(() => new AgentService(), []);
  const taskService = useMemo(() => new TaskService(), []);
  const [nodes, setNodes] = useState<WorkflowNode[]>(() =>
    initialData ? normalizeWorkflowNodes(initialData.agents) : []
  );
  const [connections, setConnections] = useState<
    { from: string; to: string }[]
  >(initialData?.connections || []);
  const [currentStep, setCurrentStep] = useState<0 | 1 | 2>(0);
  const [sidePanelWidth, setSidePanelWidth] = useState(() => {
    const storedWidth = localStorage.getItem('workflow_side_panel_width');
    const parsedWidth = storedWidth ? Number(storedWidth) : 420;
    return Number.isNaN(parsedWidth) ? 420 : parsedWidth;
  });
  const isResizingRef = useRef(false);
  const panelRef = useRef<HTMLDivElement | null>(null);
  const [selectedAgentForTaskId, setSelectedAgentForTaskId] = useState<
    number | null
  >(null);
  const [agentDraft, setAgentDraft] = useState<AgentFormValues | null>(null);
  const [agentDraftReady, setAgentDraftReady] = useState(false);
  const [editingAgentField, setEditingAgentField] = useState<
    keyof AgentFormValues | null
  >(null);
  const [editingAgentValue, setEditingAgentValue] = useState('');
  const [taskDrafts, setTaskDrafts] = useState<
    Array<{ id: string; taskId?: number; values: TaskFormValues }>
  >([]);
  const [editingExistingAgentId, setEditingExistingAgentId] = useState<
    number | null
  >(null);
  const [isLoadingAgentForEdit, setIsLoadingAgentForEdit] = useState(false);
  const [isSavingAgentEdits, setIsSavingAgentEdits] = useState(false);
  const [editingTaskDraft, setEditingTaskDraft] = useState<{
    id: string;
    field: keyof TaskFormValues;
  } | null>(null);
  const [editingTaskValue, setEditingTaskValue] = useState('');
  const [isFinalizing, setIsFinalizing] = useState(false);
  const [agentCreationMode, setAgentCreationMode] =
    useState<CreationMode>('ai');
  const [taskCreationMode, setTaskCreationMode] =
    useState<CreationMode>('ai');
  const [isCreatingTaskDraft, setIsCreatingTaskDraft] = useState(true);

  // Atualizar nodes, crewName e crewDescription quando initialData mudar
  useEffect(() => {
    if (initialData) {
      setNodes(normalizeWorkflowNodes(initialData.agents));
      setConnections(initialData.connections || []);
      setCrewName(initialData.crewName || '');
      setCrewDescription(initialData.crewDescription || '');
      setSelectedCategoryIds(initialData.category_ids || []);
    }
  }, [initialData]);
  const [showNodeModal, setShowNodeModal] = useState(false);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [nodeToDelete, setNodeToDelete] = useState<string | null>(null);
  const [editingNode, setEditingNode] = useState<WorkflowNode | null>(null);
  const [isConnecting, setIsConnecting] = useState(false);
  const [connectionStart, setConnectionStart] = useState<string | null>(null);
  const [draggedNode, setDraggedNode] = useState<WorkflowNode | null>(null);
  const [dragOffset, setDragOffset] = useState({ x: 0, y: 0 });
  const lastDragEndRef = useRef<number>(0);
  const hasDraggedRef = useRef(false);
  const [showAgentSelector, setShowAgentSelector] = useState(false);
  const [crewName, setCrewName] = useState(initialData?.crewName || '');
  const [crewDescription, setCrewDescription] = useState(
    initialData?.crewDescription || ''
  );
  const [selectedCategoryIds, setSelectedCategoryIds] = useState<number[]>(
    initialData?.category_ids || []
  );
  const [categoryOptions, setCategoryOptions] = useState<CrewCategory[]>(
    availableCategories
  );
  const [categorySearch, setCategorySearch] = useState('');
  const [isCreatingCategory, setIsCreatingCategory] = useState(false);

  const resolvedOrganizationId = organizationId ?? currentOrganization?.id;

  useEffect(() => {
    setCategoryOptions(availableCategories);
  }, [availableCategories]);

  const selectedCategories = useMemo(
    () =>
      selectedCategoryIds
        .map(id => categoryOptions.find(category => category.id === id))
        .filter((category): category is CrewCategory => Boolean(category)),
    [selectedCategoryIds, categoryOptions]
  );

  const normalizedCategorySearch = categorySearch.trim();
  const matchingCategory = useMemo(
    () =>
      categoryOptions.find(
        category =>
          category.name.toLowerCase() === normalizedCategorySearch.toLowerCase()
      ),
    [categoryOptions, normalizedCategorySearch]
  );

  const filteredCategoryOptions = useMemo(() => {
    const query = normalizedCategorySearch.toLowerCase();
    return categoryOptions.filter(category => {
      const isSelected = selectedCategoryIds.includes(category.id);
      if (isSelected) return false;
      if (!query) return true;
      return category.name.toLowerCase().includes(query);
    });
  }, [categoryOptions, normalizedCategorySearch, selectedCategoryIds]);

  const toggleCategorySelection = useCallback((categoryId: number) => {
    setSelectedCategoryIds(prev =>
      prev.includes(categoryId)
        ? prev.filter(id => id !== categoryId)
        : [...prev, categoryId]
    );
  }, []);

  const handleCreateCategory = useCallback(async () => {
    const categoryName = normalizedCategorySearch;
    if (!categoryName) {
      toast.error(t('teams.categoryNameRequired'));
      return;
    }
    if (!resolvedOrganizationId) {
      toast.error(t('teams.organizationNotFound'));
      return;
    }
    if (matchingCategory) {
      toggleCategorySelection(matchingCategory.id);
      setCategorySearch('');
      return;
    }

    try {
      setIsCreatingCategory(true);
      const createdCategory = await crewService.createCategory({
        name: categoryName,
        organization_id: resolvedOrganizationId,
      });

      setCategoryOptions(prev => {
        if (prev.some(category => category.id === createdCategory.id)) {
          return prev;
        }
        return [...prev, createdCategory].sort((a, b) =>
          a.name.localeCompare(b.name)
        );
      });
      setSelectedCategoryIds(prev =>
        prev.includes(createdCategory.id) ? prev : [...prev, createdCategory.id]
      );
      setCategorySearch('');

      if (onCategoriesUpdated) {
        await Promise.resolve(onCategoriesUpdated());
      }
      toast.success(t('teams.categoryCreatedSuccessfully'));
    } catch (error) {
      toast.error(t('teams.errorCreatingCategory'));
    } finally {
      setIsCreatingCategory(false);
    }
  }, [
    matchingCategory,
    normalizedCategorySearch,
    onCategoriesUpdated,
    resolvedOrganizationId,
    t,
    toggleCategorySelection,
  ]);

  const mapToolsToMode = (
    tools: string[] | undefined
  ): 'creative' | 'accuracy' => {
    if (!Array.isArray(tools) || tools.length === 0) {
      return 'accuracy';
    }
    const normalizedTools = tools.map(tool => (tool || '').toLowerCase().trim());
    const hasRagTool = normalizedTools.includes('ragtool');
    const hasCodeInterpreterTool = normalizedTools.includes('codeinterpretertool');

    if (hasRagTool && !hasCodeInterpreterTool) {
      return 'creative';
    }
    if (hasCodeInterpreterTool && !hasRagTool) {
      return 'accuracy';
    }
    return 'accuracy';
  };

  const mapModeToTools = (mode: 'creative' | 'accuracy'): string[] =>
    mode === 'creative' ? [...ENABLED_CREW_TOOLS] : [...ENABLED_CREW_TOOLS];

  const normalizeTaskContext = (value: unknown): string[] => {
    if (Array.isArray(value)) {
      return value
        .map(item => String(item ?? '').trim())
        .filter(Boolean);
    }
    if (typeof value === 'string') {
      return value
        .split(',')
        .map(item => item.trim())
        .filter(Boolean);
    }
    return [];
  };

  const defaultAgentFormValues = useMemo<AgentFormValues>(
    () => ({
      name: '',
      role: '',
      goal: '',
      backstory: '',
      tools: [],
      tool_mode: 'accuracy',
      verbose: false,
      allow_delegation: false,
      max_iter: DEFAULT_MAX_ITER,
      max_execution_time: DEFAULT_MAX_EXECUTION_TIME,
      is_active: true,
    }),
    []
  );

  const defaultTaskFormValues = useMemo<TaskFormValues>(
    () => ({
      description: '',
      expected_output: '',
      tools: [],
      tool_mode: 'accuracy',
      context: [],
      is_active: true,
      result_format: '',
    }),
    []
  );

  /**
   * Garante que o nó tenha agentId. Deriva de node.id quando no formato agent_X.
   */
  const ensureNodeAgentId = useCallback((node: WorkflowNode): WorkflowNode => {
    if (node.agentId != null) return node;
    const match = node.id.match(/^agent_(\d+)$/);
    if (match) {
      return { ...node, agentId: parseInt(match[1], 10) };
    }
    return node;
  }, []);

  /**
   * Constrói um nó de agente para o canvas.
   * @param agent Agente selecionado
   * @param index Índice base para posicionamento
   */
  const buildAgentNode = (agent: Agent, index: number): WorkflowNode => ({
    id: `agent_${agent.id}`,
    type: 'agent',
    name: agent.name,
    description: agent.backstory || '',
    role: agent.role || '',
    goal: agent.goal || '',
    tools: agent.tools || [],
    x: 50 + (index % 3) * 250,
    y: 50 + Math.floor(index / 3) * 150,
    agentId: agent.id,
  });

  const addAgentsFromSelector = (agents: Agent[]) => {
    const existingIds = new Set(
      nodes.map(node => node.agentId).filter((id): id is number => !!id)
    );
    const agentsToAdd = agents.filter(agent => !existingIds.has(agent.id));
    if (agentsToAdd.length === 0) {
      return;
    }

    const startIndex = nodes.length;
    const newNodes = agentsToAdd.map((agent, index) =>
      buildAgentNode(agent, startIndex + index)
    );

    setNodes(prev => [...prev, ...newNodes]);
    if (!selectedAgentForTaskId && agentsToAdd[0]) {
      setSelectedAgentForTaskId(agentsToAdd[0].id);
    }
  };

  const deleteNode = (nodeId: string) => {
    const nodeToRemove = nodes.find(node => node.id === nodeId);
    const removedAgentId = nodeToRemove?.agentId;
    const remainingAgentIds = nodes
      .filter(node => node.id !== nodeId)
      .map(node => node.agentId)
      .filter((id): id is number => !!id);

    setNodes(prev => prev.filter(node => node.id !== nodeId));
    setConnections(prev =>
      prev.filter(conn => conn.from !== nodeId && conn.to !== nodeId)
    );

    if (removedAgentId && selectedAgentForTaskId === removedAgentId) {
      setSelectedAgentForTaskId(remainingAgentIds[0] || null);
    }
  };

  const startConnection = (nodeId: string) => {
    setIsConnecting(true);
    setConnectionStart(nodeId);
  };

  const handleNodeClick = (node: WorkflowNode) => {
    if (isConnecting && connectionStart && connectionStart !== node.id) {
      const newConnection = { from: connectionStart, to: node.id };

      if (
        !connections.some(
          conn => conn.from === connectionStart && conn.to === node.id
        )
      ) {
        setConnections(prev => {
          const newConnections = [...prev, newConnection];
          return newConnections;
        });
        toast.success('Conexão criada com sucesso!');
      } else {
        toast('Conexão já existe', { icon: 'ℹ️' });
      }
      setIsConnecting(false);
      setConnectionStart(null);
    }
  };

  const handleNodeDoubleClick = useCallback(
    async (node: WorkflowNode) => {
      if (!node.agentId) {
        toast.error(t('workflow.agentIdRequired'));
        return;
      }
      setIsLoadingAgentForEdit(true);
      setEditingExistingAgentId(node.agentId);
      setSelectedAgentForTaskId(node.agentId);
      try {
        const [agent, tasks] = await Promise.all([
          agentService.getById(node.agentId),
          taskService.getByAgent(node.agentId),
        ]);
        if (!agent) {
          toast.error(t('workflow.errorLoadingAgentForEdit'));
          setEditingExistingAgentId(null);
          return;
        }
        const agentDraftValues: AgentFormValues = {
          name: agent.name,
          role: agent.role || '',
          goal: agent.goal || '',
          backstory: agent.backstory || '',
          tools: agent.tools || [],
          tool_mode: mapToolsToMode(agent.tools),
          verbose: agent.verbose ?? false,
          allow_delegation: agent.allow_delegation ?? false,
          max_iter: String(agent.max_iter ?? DEFAULT_MAX_ITER),
          max_execution_time: String(
            agent.max_execution_time ?? DEFAULT_MAX_EXECUTION_TIME
          ),
          is_active: agent.is_active ?? true,
        };
        setAgentDraft(agentDraftValues);
        setAgentDraftReady(true);
        const drafts = (tasks || []).map(task => ({
          id: generateId(),
          taskId: task.id,
          values: {
            description: task.description || '',
            expected_output: task.expected_output || '',
            tools: task.tools || [],
            tool_mode: mapToolsToMode(task.tools),
            context: task.context || [],
            is_active: task.is_active ?? true,
            result_format: '' as const,
          } as TaskFormValues,
        }));
        setTaskDrafts(drafts);
        setIsCreatingTaskDraft(drafts.length === 0);
        setCurrentStep(1);
      } catch (error) {
        console.error('Erro ao carregar agente/tasks:', error);
        toast.error(
          `${t('workflow.errorLoadingAgentForEdit')} ${error instanceof Error ? error.message : ''}`
        );
        setEditingExistingAgentId(null);
      } finally {
        setIsLoadingAgentForEdit(false);
      }
    },
    [agentService, mapToolsToMode, taskService, t]
  );

  const handleSaveAgentEdits = async () => {
    if (!editingExistingAgentId || !agentDraft || !currentOrganization) {
      return;
    }
    setIsSavingAgentEdits(true);
    try {
      await agentService.update(editingExistingAgentId, {
        name: agentDraft.name,
        role: agentDraft.role,
        goal: agentDraft.goal,
        backstory: agentDraft.backstory,
        tools: agentDraft.tools,
        verbose: agentDraft.verbose,
        allow_delegation: agentDraft.allow_delegation,
        max_iter: DEFAULT_MAX_ITER,
        max_execution_time: DEFAULT_MAX_EXECUTION_TIME,
        is_active: agentDraft.is_active,
      });
      const savedTaskDrafts = await Promise.all(
        taskDrafts.map(async draft => {
          if (draft.taskId) {
            await taskService.update(draft.taskId, {
              description: draft.values.description,
              expected_output: draft.values.expected_output,
              tools: draft.values.tools,
              context: normalizeTaskContext(draft.values.context),
              is_active: draft.values.is_active,
            });
            return draft;
          }

          const createdTask = await taskService.create({
            description: draft.values.description,
            expected_output: draft.values.expected_output,
            tools: draft.values.tools,
            context: normalizeTaskContext(draft.values.context),
            is_active: draft.values.is_active,
            agent_id: editingExistingAgentId,
          });

          return {
            ...draft,
            taskId: createdTask.id,
          };
        })
      );
      setTaskDrafts(savedTaskDrafts);
      setNodes(prev =>
        prev.map(node =>
          node.agentId === editingExistingAgentId
            ? {
                ...node,
                name: agentDraft!.name,
                description: agentDraft!.backstory || '',
                role: agentDraft!.role || '',
                goal: agentDraft!.goal || '',
                tools: agentDraft!.tools || [],
              }
            : node
        )
      );
      toast.success(t('workflow.agentEditsSaved'));
      exitEditFlow();
    } catch (error) {
      toast.error(t('workflow.agentEditsSaveError'));
    } finally {
      setIsSavingAgentEdits(false);
    }
  };

  const handleNodeDragStart = (node: WorkflowNode, e: React.MouseEvent) => {
    // Só inicia drag se não estiver clicando nos botões
    if ((e.target as HTMLElement).closest('button')) {
      return;
    }

    e.preventDefault();
    hasDraggedRef.current = false;
    setDraggedNode(node);
    const rect = e.currentTarget.getBoundingClientRect();
    setDragOffset({
      x: e.clientX - rect.left,
      y: e.clientY - rect.top,
    });
  };

  const handleNodeDrag = (e: React.MouseEvent) => {
    if (!draggedNode) return;

    const canvas = e.currentTarget.closest('.canvas-container');
    if (!canvas) return;

    const rect = canvas.getBoundingClientRect();
    const newX = e.clientX - rect.left - dragOffset.x;
    const newY = e.clientY - rect.top - dragOffset.y;

    // Manter o nó dentro dos limites do canvas
    const boundedX = Math.max(0, Math.min(newX, rect.width - 192));
    const boundedY = Math.max(0, Math.min(newY, rect.height - 48));

    hasDraggedRef.current = true;
    setNodes(prev =>
      prev.map(node =>
        node.id === draggedNode.id
          ? { ...node, x: boundedX, y: boundedY }
          : node
      )
    );
  };

  const handleNodeDragEnd = () => {
    const wasClick = draggedNode && !hasDraggedRef.current && !isConnecting;
    const nodeToOpen = wasClick ? draggedNode : null;

    if (draggedNode && hasDraggedRef.current) {
      lastDragEndRef.current = Date.now();
    }
    setDraggedNode(null);
    setDragOffset({ x: 0, y: 0 });

    if (nodeToOpen && Date.now() - lastDragEndRef.current >= 300) {
      const nodeWithAgentId = ensureNodeAgentId(nodeToOpen);
      if (nodeWithAgentId.agentId) {
        handleNodeDoubleClick(nodeWithAgentId);
      } else {
        setEditingNode(nodeToOpen);
        setShowNodeModal(true);
      }
    }
  };

  const agentNodes = useMemo(
    () => nodes.filter(node => node.type === 'agent'),
    [nodes]
  );

  const selectedAgentNode = agentNodes.find(
    node => node.agentId === selectedAgentForTaskId
  );

  const selectedAgentContext =
    agentDraftReady && agentDraft
      ? {
          name: agentDraft.name,
          role: agentDraft.role || '',
          goal: agentDraft.goal || '',
          backstory: agentDraft.backstory || '',
          tools: agentDraft.tools || [],
        }
      : selectedAgentNode
        ? {
            name: selectedAgentNode.name,
            role: selectedAgentNode.role || '',
            goal: selectedAgentNode.goal || '',
            backstory: selectedAgentNode.description || '',
            tools: selectedAgentNode.tools || [],
          }
        : null;

  const taskQuickGeneratePrompt = useMemo(() => {
    if (!selectedAgentContext) {
      return '';
    }
    return `Gere a tarefa para execução desse agente: Nome: ${selectedAgentContext.name}; Papel: ${selectedAgentContext.role}; Objetivo: ${selectedAgentContext.goal}; Contexto: ${selectedAgentContext.backstory}; Ferramentas: ${(selectedAgentContext.tools || []).join(', ')}`;
  }, [selectedAgentContext]);

  const taskPanelDefaultValues = useMemo(
    () => ({
      ...defaultTaskFormValues,
      tool_mode: agentDraft?.tool_mode || 'accuracy',
      tools: mapModeToTools(agentDraft?.tool_mode || 'accuracy'),
    }),
    [agentDraft?.tool_mode, defaultTaskFormValues]
  );

  useEffect(() => {
    if (selectedAgentForTaskId) {
      return;
    }
    const firstAgentId = agentNodes.find(node => node.agentId)?.agentId || null;
    if (firstAgentId) {
      setSelectedAgentForTaskId(firstAgentId);
    }
  }, [agentNodes, selectedAgentForTaskId]);

  useEffect(() => {
    localStorage.setItem('workflow_side_panel_width', String(sidePanelWidth));
  }, [sidePanelWidth]);

  const stopResizing = useCallback(() => {
    if (!isResizingRef.current) {
      return;
    }
    isResizingRef.current = false;
    document.body.style.cursor = '';
    document.body.style.userSelect = '';
  }, []);

  const handleMouseMove = useCallback(
    (event: MouseEvent) => {
      if (!isResizingRef.current || !panelRef.current) {
        return;
      }
      const newWidth = window.innerWidth - event.clientX;
      const boundedWidth = Math.min(Math.max(newWidth, 320), 720);
      setSidePanelWidth(boundedWidth);
    },
    [setSidePanelWidth]
  );

  useEffect(() => {
    document.addEventListener('mousemove', handleMouseMove);
    document.addEventListener('mouseup', stopResizing);
    return () => {
      document.removeEventListener('mousemove', handleMouseMove);
      document.removeEventListener('mouseup', stopResizing);
    };
  }, [handleMouseMove, stopResizing]);

  /**
   * Formata valores do rascunho de agente para exibição.
   */
  const formatAgentFieldValue = (
    field: keyof AgentFormValues,
    value: AgentFormValues[keyof AgentFormValues]
  ): string => {
    if (field === 'tool_mode') {
      return value === 'creative'
        ? t('agents.modeCreative')
        : t('agents.modeAccuracy');
    }
    if (field === 'tools' && Array.isArray(value)) {
      return value.join(', ');
    }
    return value ? String(value) : '';
  };

  /**
   * Converte a edição textual para o tipo do campo de agente.
   */
  const parseAgentFieldValue = (
    field: keyof AgentFormValues,
    value: string
  ): AgentFormValues[keyof AgentFormValues] => {
    if (field === 'tools') {
      return value
        .split(',')
        .map(tool => tool.trim())
        .filter(Boolean);
    }
    if (field === 'max_iter') {
      return value.trim() === '' ? DEFAULT_MAX_ITER : value;
    }
    if (field === 'max_execution_time') {
      return value.trim() === '' ? DEFAULT_MAX_EXECUTION_TIME : value;
    }
    return value;
  };

  /**
   * Armazena o rascunho gerado pela IA.
   */
  const handleAgentGenerated = (values: AgentFormValues) => {
    setEditingExistingAgentId(null);
    setAgentDraft(values);
    setAgentDraftReady(true);
    setEditingAgentField(null);
    // Um novo agente no fluxo de criação reinicia o contexto de tasks
    setTaskDrafts([]);
    setEditingTaskDraft(null);
    setIsCreatingTaskDraft(true);
  };

  /**
   * Abre o formulário de criação de agente limpo, sem agente selecionado.
   */
  const handleCreateNewAgent = () => {
    setEditingExistingAgentId(null);
    setAgentDraft(null);
    setAgentDraftReady(false);
    setTaskDrafts([]);
    setSelectedAgentForTaskId(null);
    setEditingAgentField(null);
    setAgentCreationMode('ai');
    setTaskCreationMode('ai');
    setIsCreatingTaskDraft(true);
    setCurrentStep(1);
  };

  /**
   * Inicia a edição inline de um campo do agente.
   */
  const startEditingAgent = (field: keyof AgentFormValues) => {
    if (!agentDraft) {
      return;
    }
    setEditingAgentField(field);
    setEditingAgentValue(formatAgentFieldValue(field, agentDraft[field]));
  };

  /**
   * Salva a edição inline do agente.
   */
  const saveEditingAgent = () => {
    if (!agentDraft || !editingAgentField) {
      return;
    }
    if (editingAgentField === 'tool_mode') {
      setEditingAgentField(null);
      return;
    }
    const updatedValue = parseAgentFieldValue(
      editingAgentField,
      editingAgentValue
    );
    setAgentDraft(prev =>
      prev ? { ...prev, [editingAgentField]: updatedValue } : prev
    );
    setEditingAgentField(null);
  };

  /**
   * Formata valores do rascunho de task para exibição.
   */
  const formatTaskFieldValue = (
    field: keyof TaskFormValues,
    value: TaskFormValues[keyof TaskFormValues]
  ): string => {
    if (field === 'tool_mode') {
      return value === 'creative'
        ? t('tasks.modeCreative')
        : t('tasks.modeAccuracy');
    }
    if (field === 'tools' && Array.isArray(value)) {
      return value.join(', ');
    }
    if (field === 'context' && Array.isArray(value)) {
      return value.join(', ');
    }
    return value ? String(value) : '';
  };

  /**
   * Converte a edição textual para o tipo do campo de task.
   */
  const parseTaskFieldValue = (
    field: keyof TaskFormValues,
    value: string
  ): TaskFormValues[keyof TaskFormValues] => {
    if (field === 'context') {
      return value
        .split(',')
        .map(item => item.trim())
        .filter(Boolean);
    }
    return value;
  };

  /**
   * Sincroniza agentDraft no nó do canvas quando editando agente existente.
   */
  useEffect(() => {
    if (!editingExistingAgentId || !agentDraft || !agentDraftReady) {
      return;
    }
    setNodes(prev =>
      prev.map(node =>
        node.agentId === editingExistingAgentId
          ? {
              ...node,
              name: agentDraft.name,
              description: agentDraft.backstory || '',
              role: agentDraft.role || '',
              goal: agentDraft.goal || '',
              tools: agentDraft.tools || [],
            }
          : node
      )
    );
  }, [
    editingExistingAgentId,
    agentDraft?.name,
    agentDraft?.backstory,
    agentDraft?.role,
    agentDraft?.goal,
    agentDraft?.tools,
    agentDraftReady,
  ]);

  /**
   * Armazena uma task gerada como rascunho local.
   */
  const handleTaskGenerated = (values: TaskFormValues) => {
    const inheritedMode = agentDraft?.tool_mode || 'accuracy';
    setTaskDrafts(prev => [
      ...prev,
      {
        id: generateId(),
        values: { ...values, tool_mode: inheritedMode, tools: mapModeToTools(inheritedMode) },
      },
    ]);
    setIsCreatingTaskDraft(false);
  };

  /**
   * Atualiza um campo diretamente no rascunho da task.
   */
  const updateTaskDraftField = <K extends keyof TaskFormValues>(
    draftId: string,
    field: K,
    value: TaskFormValues[K]
  ) => {
    setTaskDrafts(prev =>
      prev.map(draft => {
        if (draft.id !== draftId) {
          return draft;
        }
        return {
          ...draft,
          values: {
            ...draft.values,
            [field]: value,
          },
        };
      })
    );
  };

  /**
   * Inicia a edição inline de um campo de task.
   */
  const startEditingTask = (draftId: string, field: keyof TaskFormValues) => {
    const draft = taskDrafts.find(item => item.id === draftId);
    if (!draft) {
      return;
    }
    setEditingTaskDraft({ id: draftId, field });
    setEditingTaskValue(formatTaskFieldValue(field, draft.values[field]));
  };

  /**
   * Salva a edição inline da task.
   */
  const saveEditingTask = () => {
    if (!editingTaskDraft) {
      return;
    }
    if (editingTaskDraft.field === 'tool_mode') {
      setEditingTaskDraft(null);
      return;
    }

    const updatedValue = parseTaskFieldValue(
      editingTaskDraft.field,
      editingTaskValue
    );

    updateTaskDraftField(
      editingTaskDraft.id,
      editingTaskDraft.field,
      updatedValue as TaskFormValues[keyof TaskFormValues]
    );

    setEditingTaskDraft(null);
  };

  /**
   * Persiste agente e tasks rascunhadas no backend.
   */
  const handleFinalizeDrafts = async (pendingTaskValues?: TaskFormValues) => {
    if (!currentOrganization) {
      toast.error(t('teams.organizationNotFound'));
      return;
    }
    if (!agentDraft) {
      toast.error(t('workflow.agentDraftRequired'));
      return;
    }
    const inheritedMode = agentDraft.tool_mode || 'accuracy';
    const draftsToPersist = pendingTaskValues
      ? [
          ...taskDrafts,
          {
            id: generateId(),
            values: {
              ...pendingTaskValues,
              tool_mode: inheritedMode,
              tools: mapModeToTools(inheritedMode),
            },
          },
        ]
      : taskDrafts;

    if (draftsToPersist.length === 0) {
      toast.error(t('workflow.atLeastOneTaskRequired'));
      return;
    }

    setIsFinalizing(true);
    try {
      const newAgent = await agentService.create({
        ...agentDraft,
        organization_id: currentOrganization.id,
        tools: agentDraft.tools,
        max_iter: DEFAULT_MAX_ITER,
        max_execution_time: DEFAULT_MAX_EXECUTION_TIME,
      });

      if (draftsToPersist.length > 0) {
        await Promise.all(
          draftsToPersist.map(taskDraft =>
            taskService.create({
              description: taskDraft.values.description,
              expected_output: taskDraft.values.expected_output,
              tools: taskDraft.values.tools,
              context: normalizeTaskContext(taskDraft.values.context),
              is_active: taskDraft.values.is_active,
              agent_id: newAgent.id,
            })
          )
        );
      }

      setNodes(prev => [...prev, buildAgentNode(newAgent, prev.length)]);
      setSelectedAgentForTaskId(newAgent.id);
      toast.success(t('workflow.draftsCreatedSuccessfully'));
      setAgentDraft(null);
      setAgentDraftReady(false);
      setTaskDrafts([]);
      setIsCreatingTaskDraft(true);
      setCurrentStep(0);
    } catch (error) {
      toast.error(t('workflow.draftsCreateError'));
    } finally {
      setIsFinalizing(false);
    }
  };

  const saveNode = (nodeData: Partial<WorkflowNode>) => {
    if (editingNode) {
      setNodes(prev =>
        prev.map(node =>
          node.id === editingNode.id ? { ...node, ...nodeData } : node
        )
      );
    }
    setShowNodeModal(false);
    setEditingNode(null);
  };

  const getNodeIcon = (type: 'agent') => {
    return Bot;
  };

  const getNodeColor = (type: 'agent') => {
    return 'bg-blue-100 border-blue-300 text-blue-800 dark:bg-blue-900/20 dark:border-blue-700 dark:text-blue-300';
  };

  // Função para calcular pontos de conexão nas bordas dos agentes (estilo Miro)
  const getConnectionPoints = (
    fromNode: WorkflowNode,
    toNode: WorkflowNode
  ) => {
    const nodeWidth = 192; // Largura do nó (w-48 = 192px)
    const nodeHeight = 120; // Altura do nó (ajustada para o tamanho real)

    // Calcular centros dos nós
    const fromCenterX = fromNode.x + nodeWidth / 2;
    const fromCenterY = fromNode.y + nodeHeight / 2;
    const toCenterX = toNode.x + nodeWidth / 2;
    const toCenterY = toNode.y + nodeHeight / 2;

    // Calcular direção da conexão
    const dx = toCenterX - fromCenterX;
    const dy = toCenterY - fromCenterY;

    // Se os nós estiverem na mesma posição, evitar divisão por zero
    if (dx === 0 && dy === 0) {
      return {
        startX: fromCenterX,
        startY: fromCenterY,
        endX: toCenterX,
        endY: toCenterY,
      };
    }

    const angle = Math.atan2(dy, dx);

    // Função auxiliar para encontrar o ponto de interseção com a borda do retângulo
    const getIntersectionPoint = (node: WorkflowNode, angle: number) => {
      const halfWidth = nodeWidth / 2;
      const halfHeight = nodeHeight / 2;

      const cos = Math.cos(angle);
      const sin = Math.sin(angle);

      let x = 0;
      let y = 0;

      // Tentar interseção com as bordas verticais (esquerda/direita)
      if (Math.abs(cos) > 1e-6) {
        // Evitar divisão por zero
        x = halfWidth;
        y = halfWidth * (sin / cos);
        if (Math.abs(y) <= halfHeight) {
          return {
            x: node.x + halfWidth + x * Math.sign(cos),
            y: node.y + halfHeight + y,
          };
        }
      }

      // Tentar interseção com as bordas horizontais (superior/inferior)
      if (Math.abs(sin) > 1e-6) {
        // Evitar divisão por zero
        y = halfHeight;
        x = halfHeight * (cos / sin);
        if (Math.abs(x) <= halfWidth) {
          return {
            x: node.x + halfWidth + x,
            y: node.y + halfHeight + y * Math.sign(sin),
          };
        }
      }

      // Fallback para o centro se nenhuma interseção for encontrada (deve ser raro)
      return { x: node.x + halfWidth, y: node.y + halfHeight };
    };

    // Calcular o ponto de saída do nó de origem
    const startPoint = getIntersectionPoint(fromNode, angle);

    // Calcular o ponto de entrada do nó de destino (ângulo oposto)
    const endPoint = getIntersectionPoint(toNode, angle + Math.PI);

    return {
      startX: startPoint.x,
      startY: startPoint.y,
      endX: endPoint.x,
      endY: endPoint.y,
    };
  };

  const handleSave = async () => {
    const agents = nodes.filter(node => node.type === 'agent');

    if (!crewName.trim() || !crewDescription.trim() || agents.length === 0) {
      toast.error(t('workflow.flowValidationRequired'));
      return;
    }

    // Calcular ordem de execução baseada nas conexões
    const executionOrder = crewOrderService.calculateExecutionOrder(
      agents,
      connections
    );

    onSave({
      agents,
      tasks: [],
      connections,
      executionOrder,
      crewName: crewName.trim(),
      crewDescription: crewDescription.trim(),
      category_ids: selectedCategoryIds,
    });
  };

  const agentDetailFields: Array<{
    key: keyof AgentFormValues;
    label: string;
    type: 'text' | 'textarea' | 'number';
  }> = [
    { key: 'name', label: t('agents.agentName'), type: 'text' },
    { key: 'role', label: t('agents.role'), type: 'text' },
    { key: 'goal', label: t('agents.goal'), type: 'textarea' },
    { key: 'backstory', label: t('agents.backstory'), type: 'textarea' },
    { key: 'tool_mode', label: t('agents.toolMode'), type: 'text' },
  ];

  const taskDetailFields: Array<{
    key: keyof TaskFormValues;
    label: string;
    type: 'text' | 'textarea';
  }> = [
    { key: 'description', label: t('tasks.taskDescription'), type: 'textarea' },
    {
      key: 'expected_output',
      label: t('tasks.expectedOutput'),
      type: 'textarea',
    },
    { key: 'result_format', label: t('tasks.resultFormat'), type: 'text' },
  ];

  const exitEditFlow = useCallback(() => {
    setEditingExistingAgentId(null);
    setCurrentStep(0);
  }, []);

  const canUseGlobalSaveAction = currentStep === 0 && !editingExistingAgentId;
  const shouldShowFooter = currentStep === 0 && !editingExistingAgentId;
  const sidePanelSteps = useMemo(
    () => [
      {
        step: 1 as const,
        label: editingExistingAgentId
          ? t('workflow.stepLabelAgent')
          : t('workflow.createAgent'),
        isDone: Boolean(agentDraftReady && agentDraft),
        canNavigate: true,
      },
      {
        step: 2 as const,
        label: editingExistingAgentId
          ? t('workflow.stepLabelTask')
          : t('workflow.createTask'),
        isDone: taskDrafts.length > 0,
        canNavigate: Boolean(editingExistingAgentId || agentDraftReady),
      },
    ],
    [
      editingExistingAgentId,
      t,
      agentDraftReady,
      agentDraft,
      taskDrafts.length,
    ]
  );

  const goToSidePanelStep = useCallback(
    (targetStep: 1 | 2) => {
      if (targetStep === 2 && !editingExistingAgentId && !agentDraftReady) {
        toast.error(t('workflow.agentDraftRequired'));
        return;
      }
      if (targetStep === 2 && !editingExistingAgentId) {
        setTaskCreationMode('ai');
        setTaskDrafts([]);
        setIsCreatingTaskDraft(true);
        setEditingTaskDraft(null);
      }
      setCurrentStep(targetStep);
    },
    [editingExistingAgentId, agentDraftReady, t]
  );

  const handleStepperBack = useCallback(() => {
    if (editingExistingAgentId) {
      if (currentStep === 2) {
        setCurrentStep(1);
        return;
      }
      exitEditFlow();
      return;
    }
    if (currentStep === 2) {
      setCurrentStep(1);
      return;
    }
    if (currentStep === 1) {
      setCurrentStep(0);
    }
  }, [currentStep, editingExistingAgentId, exitEditFlow]);

  return (
    <div className="h-full flex flex-col pb-16">
      {/* Toolbar */}
      <div className="p-4 border-b border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800">
        {/* Informações da Equipe */}
        <div className="mb-4">
          <h3 className="text-lg font-semibold text-gray-900 dark:text-white">
            {isEditing ? '' : ''}
          </h3>
          <div className="grid grid-cols-1 md:grid-cols-2 min-[2560px]:grid-cols-3 gap-4">
            <Input
              label={t('teams.teamName')}
              value={crewName}
              onChange={e => setCrewName(e.target.value)}
              placeholder="Ex: Equipe de Vendas"
              required={!isEditing}
            />
            <Input
              label="Descrição"
              value={crewDescription}
              onChange={e => setCrewDescription(e.target.value)}
              placeholder="Descreva o propósito da Equipe"
            />
            <div className="md:col-span-2 min-[2560px]:col-span-1">
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                {t('teams.categories')}
              </label>
              <div className="relative">
                <div className="w-full rounded-md border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-800 px-3 min-h-[44px] flex items-center">
                  <div className="w-full flex flex-wrap gap-2 items-center content-center">
                    {selectedCategories.map(category => (
                      <span
                        key={category.id}
                        className="inline-flex items-center gap-1 rounded-full bg-primary-50 text-primary-700 dark:bg-primary-900/40 dark:text-primary-300 px-2 py-1 text-xs"
                      >
                        {category.name}
                        <button
                          type="button"
                          onClick={() => toggleCategorySelection(category.id)}
                          className="text-primary-700 dark:text-primary-300 hover:text-primary-900 dark:hover:text-primary-100"
                          aria-label={t('teams.removeCategory')}
                        >
                          <X className="h-3 w-3" />
                        </button>
                      </span>
                    ))}
                    <input
                      value={categorySearch}
                      onChange={event => setCategorySearch(event.target.value)}
                      onKeyDown={event => {
                        if (event.key === 'Enter') {
                          event.preventDefault();
                          handleCreateCategory();
                        }
                      }}
                      placeholder={t('teams.categorySearchPlaceholder')}
                      className="h-6 leading-6 self-center w-40 sm:w-48 max-w-full bg-transparent border-0 p-0 text-sm text-gray-900 dark:text-gray-100 placeholder:text-gray-400 dark:placeholder:text-gray-500 outline-none focus:outline-none focus-visible:outline-none focus:ring-0 focus-visible:ring-0 focus:border-0"
                    />
                  </div>
                </div>

                {normalizedCategorySearch && (
                  <div className="absolute left-0 right-0 top-full mt-1 z-30 rounded-md border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800 overflow-hidden shadow-lg">
                    <div className="max-h-40 overflow-y-auto">
                      {filteredCategoryOptions.map(category => (
                        <button
                          key={category.id}
                          type="button"
                          onClick={() => toggleCategorySelection(category.id)}
                          className="w-full text-left px-3 py-2 text-sm text-gray-700 dark:text-gray-200 hover:bg-gray-50 dark:hover:bg-gray-700"
                        >
                          {category.name}
                        </button>
                      ))}
                    </div>

                    {!matchingCategory && (
                      <div className="border-t border-gray-200 dark:border-gray-700 p-2">
                        <Button
                          size="sm"
                          variant="outline"
                          onClick={handleCreateCategory}
                          disabled={isCreatingCategory}
                          className="w-full justify-center"
                        >
                          {isCreatingCategory
                            ? t('common.saving')
                            : t('teams.createCategoryFromInput', {
                                name: normalizedCategorySearch,
                              })}
                        </Button>
                      </div>
                    )}
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>

        {/* Controles do Workflow - Cancelar Conexão */}
        {isConnecting && (
          <div className="flex items-center">
            <Button
              size="sm"
              variant="outline"
              onClick={() => {
                setIsConnecting(false);
                setConnectionStart(null);
              }}
              className="bg-yellow-500 text-white hover:bg-yellow-600"
            >
              <X className="h-4 w-4 mr-2" />
              Cancelar Conexão
            </Button>
          </div>
        )}
      </div>

      {/* Canvas + Painel lateral */}
      <div className="flex-1 flex overflow-hidden">
        <div className="flex-1 relative overflow-hidden bg-gray-50 dark:bg-gray-900">
          <div
            className="w-full h-full relative canvas-container"
            style={{ height: 'calc(100vh - 200px)' }}
            onMouseMove={handleNodeDrag}
            onMouseUp={handleNodeDragEnd}
            onMouseLeave={handleNodeDragEnd}
          >
            {/* SVG para conexões */}
            <svg
              className="absolute inset-0 w-full h-full"
              style={{ pointerEvents: 'none', height: '100%', zIndex: 1 }}
            >
              <defs>
                <marker
                  id="arrowhead"
                  markerWidth="8"
                  markerHeight="6"
                  refX="7"
                  refY="3"
                  orient="auto"
                  markerUnits="strokeWidth"
                >
                  <polygon
                    points="0 0, 8 3, 0 6"
                    fill="#3B82F6"
                    stroke="#ffffff"
                    strokeWidth="1"
                    style={{
                      filter: 'drop-shadow(0 1px 2px rgba(59, 130, 246, 0.3))',
                    }}
                  />
                </marker>
              </defs>
              {connections.map((connection, index) => {
                const fromNode = nodes.find(n => n.id === connection.from);
                const toNode = nodes.find(n => n.id === connection.to);

                if (!fromNode || !toNode) {
                  return null;
                }

                // Calcular pontos de conexão nas bordas dos agentes
                const { startX, startY, endX, endY } = getConnectionPoints(
                  fromNode,
                  toNode
                );

                return (
                  <g key={`${connection.from}-${connection.to}-${index}`}>
                    {/* Linha principal com curva suave (estilo Miro) */}
                    <path
                      d={`M ${startX} ${startY} Q ${(startX + endX) / 2} ${
                        Math.min(startY, endY) - 30
                      } ${endX} ${endY}`}
                      stroke="#3B82F6"
                      strokeWidth="2"
                      fill="none"
                      markerEnd="url(#arrowhead)"
                      className="hover:stroke-blue-500 transition-colors cursor-pointer"
                      style={{
                        filter:
                          'drop-shadow(0 2px 4px rgba(59, 130, 246, 0.3))',
                      }}
                    />
                    {/* Ponto de conexão no início (estilo Miro) */}
                    <circle
                      cx={startX}
                      cy={startY}
                      r="6"
                      fill="#3B82F6"
                      stroke="#ffffff"
                      strokeWidth="2"
                      className="hover:fill-blue-500 transition-colors cursor-pointer"
                      style={{
                        filter:
                          'drop-shadow(0 2px 4px rgba(59, 130, 246, 0.3))',
                      }}
                    />
                    {/* Ponto de conexão no final (estilo Miro) */}
                    <circle
                      cx={endX}
                      cy={endY}
                      r="6"
                      fill="#3B82F6"
                      stroke="#ffffff"
                      strokeWidth="2"
                      className="hover:fill-blue-500 transition-colors cursor-pointer"
                      style={{
                        filter:
                          'drop-shadow(0 2px 4px rgba(59, 130, 246, 0.3))',
                      }}
                    />
                  </g>
                );
              })}
            </svg>

            {/* Informações do Workflow */}
            <div className="absolute top-4 left-1/2 transform -translate-x-1/2 z-20">
              <div className="flex items-center space-x-3 bg-white dark:bg-gray-800 rounded-lg shadow-lg px-4 py-2 border border-gray-200 dark:border-gray-700">
                <div className="flex items-center space-x-2">
                  <span className="text-xs font-medium text-gray-500 dark:text-gray-400">
                    Nós:
                  </span>
                  <span className="bg-blue-100 dark:bg-blue-900 text-blue-800 dark:text-blue-200 text-xs font-medium px-2 py-1 rounded-full">
                    {nodes.length}
                  </span>
                </div>
                <div className="flex items-center space-x-2">
                  <span className="text-xs font-medium text-gray-500 dark:text-gray-400">
                    Conexões:
                  </span>
                  <span className="bg-green-100 dark:bg-green-900 text-green-800 dark:text-green-200 text-xs font-medium px-2 py-1 rounded-full">
                    {connections.length}
                  </span>
                </div>
                <div className="flex items-center space-x-2">
                  <span className="text-xs font-medium text-gray-500 dark:text-gray-400">
                    Ordem:
                  </span>
                  <span className="bg-purple-100 dark:bg-purple-900 text-purple-800 dark:text-purple-200 text-xs font-medium px-2 py-1 rounded-full">
                    {crewOrderService
                      .calculateExecutionOrder(nodes, connections)
                      .map(
                        (o: { agent_id: number; execution_order: number }) =>
                          o.agent_id
                      )
                      .join(' → ') || 'N/A'}
                  </span>
                </div>
              </div>
            </div>

            {/* Indicador de modo de conexão */}
            {isConnecting && (
              <div className="absolute top-16 left-1/2 transform -translate-x-1/2 bg-yellow-500 text-white px-4 py-2 rounded-lg shadow-lg z-20">
                <p className="text-sm font-medium">
                  Modo Conexão: Clique em outro nó para conectar
                </p>
              </div>
            )}

            {/* Nós */}
            {nodes.map(node => {
              const Icon = getNodeIcon(node.type);
              const isConnectingFrom = connectionStart === node.id;
              const isDragging = draggedNode?.id === node.id;
              const isEditingThisAgent =
                editingExistingAgentId != null &&
                (node.agentId === editingExistingAgentId ||
                  ensureNodeAgentId(node).agentId === editingExistingAgentId);

              return (
                <div
                  key={node.id}
                  className={`absolute w-48 p-3 border-2 rounded-lg cursor-move transition-all z-20 select-none ${
                    isConnectingFrom
                      ? 'border-yellow-500 shadow-lg'
                      : isEditingThisAgent
                        ? 'border-primary-500 ring-2 ring-primary-200 dark:ring-primary-800 shadow-lg'
                        : isDragging
                          ? 'shadow-2xl scale-105'
                          : getNodeColor(node.type)
                  }`}
                  style={{
                    left: `${node.x}px`,
                    top: `${node.y}px`,
                    minHeight: '48px',
                  }}
                  onClick={e => {
                    e.stopPropagation();
                    if (
                      isConnecting &&
                      connectionStart &&
                      connectionStart !== node.id
                    ) {
                      handleNodeClick(node);
                    } else if (Date.now() - lastDragEndRef.current < 300) {
                      // Evitar abrir edição imediatamente após arrastar o nó
                      return;
                    }
                    const nodeWithAgentId = ensureNodeAgentId(node);
                    if (nodeWithAgentId.agentId) {
                      handleNodeDoubleClick(nodeWithAgentId);
                    } else {
                      setEditingNode(node);
                      setShowNodeModal(true);
                    }
                  }}
                  onMouseDown={e => handleNodeDragStart(node, e)}
                >
                  <div className="flex items-center justify-between mb-2">
                    <div className="flex items-center space-x-2">
                      <Icon className="h-4 w-4" />
                      <Badge variant="secondary" size="sm">
                        Agente
                      </Badge>
                    </div>
                    <div className="flex items-center space-x-1">
                      <button
                        className={`p-2 rounded-full transition-colors ${
                          isConnecting && connectionStart === node.id
                            ? 'bg-yellow-500 text-white'
                            : 'bg-blue-500 hover:bg-blue-600 text-white'
                        }`}
                        onClick={e => {
                          e.stopPropagation();
                          if (isConnecting && connectionStart === node.id) {
                            setIsConnecting(false);
                            setConnectionStart(null);
                          } else {
                            startConnection(node.id);
                          }
                        }}
                        title={
                          isConnecting && connectionStart === node.id
                            ? 'Cancelar conexão'
                            : 'Conectar'
                        }
                      >
                        <ArrowRight className="h-3 w-3" />
                      </button>
                      <button
                        className="p-2 bg-red-500 hover:bg-red-600 text-white rounded-full transition-colors"
                        onClick={e => {
                          e.stopPropagation();
                          setNodeToDelete(node.id);
                          setShowDeleteModal(true);
                        }}
                        title="Remover agente da Equipe"
                      >
                        <Trash2 className="h-3 w-3" />
                      </button>
                    </div>
                  </div>

                  <div>
                    <h4 className="font-medium text-sm truncate">
                      {node.name}
                    </h4>
                    {node.role && (
                      <p className="text-xs text-gray-600 dark:text-gray-400 truncate">
                        {node.role}
                      </p>
                    )}
                    {node.description && (
                      <p className="text-xs text-gray-600 dark:text-gray-400 truncate mt-1">
                        {node.description}
                      </p>
                    )}
                    <div className="mt-2 text-xs text-gray-500 dark:text-gray-400">
                      <p>{t('workflow.clickToEdit')}</p>
                      <p>{t('workflow.useButtonsToConnect')}</p>
                    </div>
                  </div>
                </div>
              );
            })}

            {/* Instruções */}
            {nodes.length === 0 && (
              <div className="absolute inset-0 flex items-center justify-center">
                <div className="text-center">
                  <div className="text-gray-400 dark:text-gray-600 mb-4">
                    <Bot className="h-12 w-12 mx-auto" />
                  </div>
                  <p className="text-sm text-gray-500 dark:text-gray-400">
                    {t('workflow.useButtonsToAddAgents')}
                  </p>
                </div>
              </div>
            )}
          </div>
        </div>

        <div
          className="w-1 bg-transparent cursor-col-resize"
          onMouseDown={() => {
            isResizingRef.current = true;
            document.body.style.cursor = 'col-resize';
            document.body.style.userSelect = 'none';
          }}
        />
        <div
          className="border-l border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-900 flex flex-col"
          style={{ width: `${sidePanelWidth}px` }}
          ref={panelRef}
        >
          {(currentStep > 0 || editingExistingAgentId) && (
            <div className="px-4 pt-3 pb-0 border-b border-gray-200 dark:border-gray-700">
              <div className="flex items-center justify-between gap-12 min-h-[76px]">
                <div className="flex items-center gap-4 flex-1 min-w-0 pr-8 self-center">
                  <Button
                    size="sm"
                    variant="outline"
                    onClick={handleStepperBack}
                    className="shrink-0"
                  >
                    <ArrowLeft className="h-4 w-4 mr-1" />
                    {t('common.back')}
                  </Button>
                  {sidePanelSteps.map((stepItem, index) => {
                    const isActive = currentStep === stepItem.step;
                    const isDone = stepItem.isDone;
                    const isClickable =
                      stepItem.canNavigate && !isLoadingAgentForEdit;

                    return (
                      <React.Fragment key={stepItem.step}>
                        {index > 0 && (
                          <div
                            className={`h-0.5 flex-1 ${
                              stepItem.step <= currentStep
                                ? 'bg-primary-500'
                                : 'bg-gray-200 dark:bg-gray-700'
                            }`}
                          />
                        )}
                        <button
                          type="button"
                          onClick={() => goToSidePanelStep(stepItem.step)}
                          disabled={!isClickable}
                          className="flex h-14 flex-col items-center justify-center gap-1 min-w-[78px] disabled:cursor-not-allowed"
                        >
                          <span
                            className={`h-7 w-7 rounded-full border-2 flex items-center justify-center text-xs font-semibold transition-colors ${
                              isDone
                                ? 'border-primary-600 bg-primary-600 text-white'
                                : isActive
                                  ? 'border-primary-500 bg-white dark:bg-gray-900 text-primary-600 dark:text-primary-300'
                                  : 'border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-900 text-gray-500 dark:text-gray-400'
                            }`}
                          >
                            {isDone ? '✓' : stepItem.step}
                          </span>
                          <span
                            className={`text-[11px] text-center font-medium leading-tight ${
                              isActive || isDone
                                ? 'text-gray-900 dark:text-white'
                                : 'text-gray-500 dark:text-gray-400'
                            }`}
                          >
                            {stepItem.label}
                          </span>
                        </button>
                      </React.Fragment>
                    );
                  })}
                </div>

                {(currentStep === 1 || currentStep === 2) && !editingExistingAgentId && (
                  <div className="inline-flex h-10 rounded-lg border border-gray-300 dark:border-gray-600 overflow-hidden shrink-0 self-center">
                    <button
                      type="button"
                      className={`px-3 text-sm ${
                        (currentStep === 1 ? agentCreationMode : taskCreationMode) ===
                        'manual'
                          ? 'bg-primary-600 text-white'
                          : 'bg-white dark:bg-gray-900 text-gray-700 dark:text-gray-300'
                      }`}
                      onClick={() =>
                        currentStep === 1
                          ? setAgentCreationMode('manual')
                          : setTaskCreationMode('manual')
                      }
                    >
                      Manual
                    </button>
                    <button
                      type="button"
                      className={`px-3 text-sm ${
                        (currentStep === 1 ? agentCreationMode : taskCreationMode) ===
                        'ai'
                          ? 'bg-primary-600 text-white'
                          : 'bg-white dark:bg-gray-900 text-gray-700 dark:text-gray-300'
                      }`}
                      onClick={() =>
                        currentStep === 1
                          ? setAgentCreationMode('ai')
                          : setTaskCreationMode('ai')
                      }
                    >
                      IA
                    </button>
                  </div>
                )}
              </div>

            </div>
          )}

          {isLoadingAgentForEdit && (
            <div className="h-full flex flex-col justify-center items-center p-8">
              <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary-500 mb-4" />
              <p className="text-sm text-gray-600 dark:text-gray-400">
                {t('workflow.loadingAgentForEdit')}
              </p>
            </div>
          )}

          {currentStep === 0 &&
            !editingExistingAgentId &&
            !isLoadingAgentForEdit && (
              <div className="h-full flex flex-col justify-center p-4">
                <div className="flex flex-col gap-3">
                  <Button
                    variant="outline"
                    onClick={() => setShowAgentSelector(true)}
                    disabled={isConnecting}
                    className="w-full justify-center"
                  >
                    <Plus className="h-4 w-4 mr-2" />
                    {t('workflow.addAgent')}
                  </Button>
                  <Button
                    variant="outline"
                    onClick={handleCreateNewAgent}
                    className="w-full justify-center"
                  >
                    <Bot className="h-4 w-4 mr-2" />
                    {t('workflow.createNewAgent')}
                  </Button>
                </div>
              </div>
            )}

          {currentStep === 1 && (
            <div className="h-full overflow-y-auto p-2 pb-10 space-y-5">
              {!agentDraftReady && (
                <div className="pt-4">
                  <AgentFormPanel
                    mode="create"
                    organizationId={currentOrganization?.id}
                    defaultValues={defaultAgentFormValues}
                    submitLabel={t('workflow.next')}
                    onSubmit={values => {
                      handleAgentGenerated(values);
                    }}
                    layout="stack"
                    creationMode={agentCreationMode}
                    showSubmitButton={agentCreationMode === 'manual'}
                    showForm={agentCreationMode === 'manual'}
                    onGenerateSuccess={handleAgentGenerated}
                  />
                </div>
              )}
              {agentDraftReady && agentDraft && (
                <div className="pt-4">
                  <h4 className="text-sm font-semibold text-gray-900 dark:text-white mb-3">
                    {t('workflow.agentDetails')}
                  </h4>
                  <div className="space-y-3">
                    {agentDetailFields.map(field => {
                      const isEditing = editingAgentField === field.key;
                      const displayValue = formatAgentFieldValue(
                        field.key,
                        agentDraft[field.key]
                      );
                      return (
                        <div
                          key={field.key}
                          className="border border-gray-200 dark:border-gray-700 rounded-lg p-3 bg-white dark:bg-gray-800"
                        >
                          <div className="flex items-start justify-between gap-4">
                            <div className="flex-1">
                              <p className="text-xs font-medium text-gray-600 dark:text-gray-400">
                                {field.label}
                              </p>
                              {field.key === 'tool_mode' ? (
                                isEditing ? (
                                  <div className="mt-2 grid grid-cols-1 sm:grid-cols-2 gap-2">
                                    <Button
                                      type="button"
                                      variant={
                                        agentDraft.tool_mode === 'creative'
                                          ? 'accent'
                                          : 'outline'
                                      }
                                      onClick={() =>
                                        setAgentDraft(prev =>
                                          prev
                                            ? {
                                                ...prev,
                                                tool_mode: 'creative',
                                                tools: mapModeToTools('creative'),
                                              }
                                            : prev
                                        )
                                      }
                                    >
                                      {t('agents.modeCreative')}
                                    </Button>
                                    <Button
                                      type="button"
                                      variant={
                                        agentDraft.tool_mode === 'accuracy'
                                          ? 'accent'
                                          : 'outline'
                                      }
                                      onClick={() =>
                                        setAgentDraft(prev =>
                                          prev
                                            ? {
                                                ...prev,
                                                tool_mode: 'accuracy',
                                                tools: mapModeToTools('accuracy'),
                                              }
                                            : prev
                                        )
                                      }
                                    >
                                      {t('agents.modeAccuracy')}
                                    </Button>
                                  </div>
                                ) : (
                                  <Badge
                                    variant="secondary"
                                    className="mt-2 inline-flex items-center border-primary-300 bg-primary-50 text-primary-700 dark:border-primary-700 dark:bg-primary-900/30 dark:text-primary-300"
                                  >
                                    {displayValue}
                                  </Badge>
                                )
                              ) : isEditing ? (
                                field.type === 'textarea' ? (
                                  <textarea
                                    value={editingAgentValue}
                                    onChange={event =>
                                      setEditingAgentValue(event.target.value)
                                    }
                                    rows={20}
                                    className="mt-2 w-full px-3 py-2 border border-light-300 dark:border-graphite-600 bg-white dark:bg-graphite-800 text-deepTechBlue-500 dark:text-light-50 rounded-md focus:outline-none focus:ring-primary-500 focus:border-primary-500"
                                  />
                                ) : (
                                  <Input
                                    type={field.type}
                                    value={editingAgentValue}
                                    onChange={event =>
                                      setEditingAgentValue(event.target.value)
                                    }
                                    className="mt-2"
                                  />
                                )
                              ) : (
                                <p className="mt-2 text-sm text-gray-800 dark:text-gray-200 whitespace-pre-wrap">
                                  {displayValue || '-'}
                                </p>
                              )}
                            </div>
                            <div className="mt-4">
                              {isEditing ? (
                                <Button
                                  type="button"
                                  size="sm"
                                  onClick={e => {
                                    e.preventDefault();
                                    e.stopPropagation();
                                    saveEditingAgent();
                                  }}
                                >
                                  {t('workflow.save')}
                                </Button>
                              ) : (
                                <Button
                                  size="sm"
                                  variant="outline"
                                  onClick={() => startEditingAgent(field.key)}
                                >
                                  {t('workflow.edit')}
                                </Button>
                              )}
                            </div>
                          </div>
                        </div>
                      );
                    })}
                  </div>
                </div>
              )}
              {agentDraftReady && agentDraft && !editingExistingAgentId && (
                <div className="pt-2">
                  <Button
                    variant="primary"
                    className="w-full"
                    onClick={() => {
                      setTaskCreationMode('ai');
                      setTaskDrafts([]);
                      setIsCreatingTaskDraft(true);
                      setEditingTaskDraft(null);
                      setCurrentStep(2);
                    }}
                  >
                    {t('workflow.next')}
                  </Button>
                </div>
              )}
              {editingExistingAgentId && (
                <div className="pt-2">
                  <Button
                    variant="primary"
                    className="w-full"
                    onClick={handleSaveAgentEdits}
                    disabled={isSavingAgentEdits}
                  >
                    {isSavingAgentEdits ? t('common.saving') : t('workflow.saveChanges')}
                  </Button>
                </div>
              )}
            </div>
          )}

          {currentStep === 2 && !isLoadingAgentForEdit && (
            <div className="h-full overflow-y-auto p-2 pb-10 space-y-5">
              {agentDraft && (
                <div className="border border-gray-200 dark:border-gray-700 rounded-lg p-3 bg-gray-50 dark:bg-gray-800">
                  <p className="text-xs font-medium text-gray-600 dark:text-gray-400">
                    {t('workflow.agentResponsible')}
                  </p>
                  <p className="text-sm text-gray-900 dark:text-gray-100 mt-1">
                    {agentDraft.name || t('workflow.agentDraftUnnamed')}
                  </p>
                </div>
              )}

              {(!editingExistingAgentId ||
                taskDrafts.length === 0 ||
                isCreatingTaskDraft) && (
                <div className="pt-4">
                  <TaskFormPanel
                    mode="create"
                    organizationId={currentOrganization?.id}
                    defaultValues={taskPanelDefaultValues}
                    lockToolMode={agentDraft?.tool_mode || 'accuracy'}
                    submitLabel={
                      editingExistingAgentId
                        ? t('workflow.createTask')
                        : t('workflow.finalize')
                    }
                    onSubmit={async values => {
                      if (editingExistingAgentId) {
                        handleTaskGenerated(values);
                        return;
                      }
                      await handleFinalizeDrafts(values);
                    }}
                    layout="stack"
                    agentContext={selectedAgentContext}
                    creationMode={taskCreationMode}
                    showSubmitButton={true}
                    onGenerateSuccess={
                      editingExistingAgentId ? handleTaskGenerated : undefined
                    }
                    quickGeneratePrompt={taskQuickGeneratePrompt}
                    quickGenerateLabel="Gerar tarefa para esse agente"
                  />
                </div>
              )}

              {editingExistingAgentId &&
                taskDrafts.length > 0 &&
                !isCreatingTaskDraft && (
                <div className="pt-4">
                  <h4 className="text-sm font-semibold text-gray-900 dark:text-white mb-3">
                    {t('workflow.taskDetails')}
                  </h4>
                  {!isCreatingTaskDraft && (
                    <div className="mb-3">
                      <Button
                        size="sm"
                        variant="outline"
                        onClick={() => setIsCreatingTaskDraft(true)}
                      >
                        <Plus className="h-4 w-4 mr-2" />
                        Adicionar task
                      </Button>
                    </div>
                  )}
                  <div className="space-y-4">
                    {taskDrafts.map((taskDraft, index) => (
                      <div
                        key={taskDraft.id}
                        className="border border-gray-200 dark:border-gray-700 rounded-lg p-4 bg-white dark:bg-gray-800"
                      >
                        <div className="flex items-center justify-between mb-3">
                          <p className="text-xs font-semibold text-gray-600 dark:text-gray-400">
                            {t('workflow.taskLabel')} {index + 1}
                          </p>
                        </div>
                        <div className="space-y-3">
                          {taskDetailFields.map(field => {
                            const isEditing =
                              editingTaskDraft?.id === taskDraft.id &&
                              editingTaskDraft.field === field.key;
                            const displayValue = formatTaskFieldValue(
                              field.key,
                              taskDraft.values[field.key]
                            );

                            return (
                              <div
                                key={`${taskDraft.id}-${field.key}`}
                                className="border border-gray-200 dark:border-gray-700 rounded-lg p-3"
                              >
                                <div className="flex items-start justify-between gap-4">
                                  <div className="flex-1">
                                    <p className="text-xs font-medium text-gray-600 dark:text-gray-400">
                                      {field.label}
                                    </p>
                                    {field.key === 'tool_mode' ? (
                                      isEditing ? (
                                        <div className="mt-2 grid grid-cols-1 sm:grid-cols-2 gap-2">
                                          <Button
                                            type="button"
                                            variant={
                                              taskDraft.values.tool_mode === 'creative'
                                                ? 'accent'
                                                : 'outline'
                                            }
                                            onClick={() => {
                                              updateTaskDraftField(
                                                taskDraft.id,
                                                'tool_mode',
                                                'creative'
                                              );
                                              updateTaskDraftField(
                                                taskDraft.id,
                                                'tools',
                                                mapModeToTools('creative')
                                              );
                                            }}
                                          >
                                            {t('tasks.modeCreative')}
                                          </Button>
                                          <Button
                                            type="button"
                                            variant={
                                              taskDraft.values.tool_mode === 'accuracy'
                                                ? 'accent'
                                                : 'outline'
                                            }
                                            onClick={() => {
                                              updateTaskDraftField(
                                                taskDraft.id,
                                                'tool_mode',
                                                'accuracy'
                                              );
                                              updateTaskDraftField(
                                                taskDraft.id,
                                                'tools',
                                                mapModeToTools('accuracy')
                                              );
                                            }}
                                          >
                                            {t('tasks.modeAccuracy')}
                                          </Button>
                                        </div>
                                      ) : (
                                        <Badge
                                          variant="secondary"
                                          className="mt-2 inline-flex items-center border-primary-300 bg-primary-50 text-primary-700 dark:border-primary-700 dark:bg-primary-900/30 dark:text-primary-300"
                                        >
                                          {displayValue}
                                        </Badge>
                                      )
                                    ) : field.key === 'result_format' && isEditing ? (
                                      <select
                                        value={
                                          (taskDraft.values.result_format as string) || ''
                                        }
                                        onChange={event =>
                                          updateTaskDraftField(
                                            taskDraft.id,
                                            'result_format',
                                            (event.target.value || '') as
                                              | ''
                                              | 'json'
                                              | 'text'
                                              | 'markdown'
                                          )
                                        }
                                        className="mt-2 select-control"
                                      >
                                        <option value="">
                                          {t('tasks.selectResultFormat')}
                                        </option>
                                        <option value="json">
                                          {t('tasks.resultFormatJson')}
                                        </option>
                                        <option value="text">
                                          {t('tasks.resultFormatText')}
                                        </option>
                                        <option value="markdown">
                                          {t('tasks.resultFormatMarkdown')}
                                        </option>
                                      </select>
                                    ) : isEditing ? (
                                      field.type === 'textarea' ? (
                                        <textarea
                                          value={editingTaskValue}
                                          onChange={event =>
                                            setEditingTaskValue(event.target.value)
                                          }
                                          rows={20}
                                          className="mt-2 w-full px-3 py-2 border border-light-300 dark:border-graphite-600 bg-white dark:bg-graphite-800 text-deepTechBlue-500 dark:text-light-50 rounded-md focus:outline-none focus:ring-primary-500 focus:border-primary-500"
                                        />
                                      ) : (
                                        <Input
                                          value={editingTaskValue}
                                          onChange={event =>
                                            setEditingTaskValue(event.target.value)
                                          }
                                          className="mt-2"
                                        />
                                      )
                                    ) : (
                                      <p className="mt-2 text-sm text-gray-800 dark:text-gray-200 whitespace-pre-wrap">
                                        {displayValue || '-'}
                                      </p>
                                    )}
                                  </div>
                                  <div className="mt-4">
                                    {isEditing ? (
                                      <Button
                                        type="button"
                                        size="sm"
                                        onClick={event => {
                                          event.preventDefault();
                                          event.stopPropagation();
                                          saveEditingTask();
                                        }}
                                      >
                                        {t('workflow.save')}
                                      </Button>
                                    ) : (
                                      <Button
                                        size="sm"
                                        variant="outline"
                                        onClick={() =>
                                          startEditingTask(taskDraft.id, field.key)
                                        }
                                      >
                                        {t('workflow.edit')}
                                      </Button>
                                    )}
                                  </div>
                                </div>
                              </div>
                            );
                          })}
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              )}
              {editingExistingAgentId && (
                <div className="pt-2">
                  <Button
                    variant="primary"
                    className="w-full"
                    onClick={handleSaveAgentEdits}
                    disabled={isSavingAgentEdits}
                  >
                    {isSavingAgentEdits ? t('common.saving') : t('workflow.saveChanges')}
                  </Button>
                </div>
              )}
            </div>
          )}
        </div>
      </div>

      {/* Node Edit Modal */}
      <Modal
        isOpen={showNodeModal}
        onClose={() => {
          setShowNodeModal(false);
          setEditingNode(null);
        }}
        title={`Editar Agente: ${editingNode?.name || 'Sem nome'}`}
        size="md"
      >
        {editingNode && (
          <NodeEditForm
            node={editingNode}
            onSave={saveNode}
            onCancel={() => {
              setShowNodeModal(false);
              setEditingNode(null);
            }}
          />
        )}
      </Modal>

      {/* Agent Selector Modal */}
      <AgentSelector
        isOpen={showAgentSelector}
        onClose={() => setShowAgentSelector(false)}
        onSelectAgents={addAgentsFromSelector}
        selectedAgentIds={agentNodes
          .map(node => node.agentId)
          .filter((id): id is number => !!id)}
      />

      {/* Delete Node Confirmation Modal */}
      <ConfirmModal
        isOpen={showDeleteModal}
        onClose={() => {
          setShowDeleteModal(false);
          setNodeToDelete(null);
        }}
        onConfirm={() => {
          if (nodeToDelete) {
            const node = nodes.find(n => n.id === nodeToDelete);
            deleteNode(nodeToDelete);
            setShowDeleteModal(false);
            setNodeToDelete(null);
            if (node) {
              toast.success(`Agente "${node.name}" removido da Equipe`);
            }
          }
        }}
        message={
          nodeToDelete
            ? `Tem certeza que deseja remover o agente "${
                nodes.find(n => n.id === nodeToDelete)?.name || ''
              }" da Equipe?`
            : 'Tem certeza que deseja remover este agente da Equipe?'
        }
        variant="danger"
      />

      {/* Footer fixo no bottom (apenas na etapa de criar/selecionar agentes) */}
      {shouldShowFooter && (
        <footer className="fixed bottom-0 left-0 right-0 border-t border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800 px-4 py-3 flex justify-end gap-2 z-50">
          <Button variant="outline" onClick={onCancel}>
            {t('common.cancel')}
          </Button>
          {canUseGlobalSaveAction && (
            <Button onClick={handleSave}>
              <Save className="h-4 w-4 mr-2" />
              {isEditing ? t('teams.updateTeam') : t('teams.createTeam')}
            </Button>
          )}
        </footer>
      )}
    </div>
  );
};

interface NodeEditFormProps {
  node: WorkflowNode;
  onSave: (nodeData: Partial<WorkflowNode>) => void;
  onCancel: () => void;
}

interface NodeEditFormValues {
  name: string;
  description: string;
  role: string;
  goal: string;
}

const NodeEditForm: React.FC<NodeEditFormProps> = ({
  node,
  onSave,
  onCancel,
}) => {
  const {
    register,
    handleSubmit,
    formState: { errors },
    reset,
  } = useForm<NodeEditFormValues>({
    defaultValues: {
      name: node.name,
      description: node.description || '',
      role: node.role || '',
      goal: node.goal || '',
    },
  });

  useEffect(() => {
    reset({
      name: node.name,
      description: node.description || '',
      role: node.role || '',
      goal: node.goal || '',
    });
  }, [node, reset]);

  return (
    <form onSubmit={handleSubmit(onSave)} className="space-y-4">
      <div className="bg-blue-50 dark:bg-blue-900/20 p-3 rounded-lg mb-4">
        <p className="text-sm text-blue-800 dark:text-blue-200">
          <strong>Nota:</strong> Esta edição afeta apenas a visualização no
          workflow. Para alterar os dados originais do agente, edite-o na seção
          de Agentes.
        </p>
      </div>

      <Input
        label="Nome"
        error={errors.name?.message}
        placeholder="Nome do agente"
        {...register('name', { required: 'Nome é obrigatório' })}
      />

      <Input
        label="Descrição"
        placeholder="Descrição detalhada"
        {...register('description')}
      />

      {node.type === 'agent' && (
        <>
          <Input
            label="Função/Especialização"
            placeholder="Ex: Especialista em vendas"
            {...register('role')}
          />

          <Input
            label="Objetivo Principal"
            placeholder="Ex: Aumentar vendas em 20%"
            {...register('goal')}
          />
        </>
      )}

      <div className="flex justify-end space-x-2 pt-4">
        <Button type="button" variant="outline" onClick={onCancel}>
          Cancelar
        </Button>
        <Button type="submit" variant="primary">
          Salvar
        </Button>
      </div>
    </form>
  );
};
